// src/Redux/Project/projectSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ROUTES } from '../Constants/apiRoutes';
import axiosWithAuth from '../UtilsRedux/axiosWithAuth';
import { showFeedback } from '../UI/uiFeedbackSlice';

const token = localStorage.getItem('token');

export const createProject = createAsyncThunk(
  'projects/create',
  async (data, thunkAPI) => {
    const { dispatch } = thunkAPI;
    try {
      const res = await axiosWithAuth.post(API_ROUTES.PROJECTS, data);
      const payload = res.data?.data;
      const backendMessage = res.data?.message; // ← use server message

      dispatch(
        showFeedback({
          type: 'success',
          title: 'Project created',
          message: backendMessage || 'Project created successfully.',
        })
      );

      return payload;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Error creating project';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Project creation failed',
          message,
        })
      );

      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchProjects = createAsyncThunk(
  'projects/fetchAll',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.get(API_ROUTES.PROJECTS);
      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Error fetching projects';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Could not load projects',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

export const fetchProjectById = createAsyncThunk(
  'projects/fetchById',
  async (projectId, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.get(`${API_ROUTES.PROJECTS}/${projectId}`);
      return res.data.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Error fetching project details';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Could not load project',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);


export const updateProjectDetails = createAsyncThunk(
  'projects/updateProjectDetails',
  async ({ id, updates }, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.put(`${API_ROUTES.PROJECTS}/${id}`, updates);
      const payload = res.data?.data;
      const backendMessage = res.data?.message;

      dispatch(
        showFeedback({
          type: 'success',
          title: 'Project updated',
          message: backendMessage || 'Changes saved successfully.',
        })
      );

      return payload;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Update failed';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Project update failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);


export const deleteProject = createAsyncThunk(
  'projects/delete',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const res = await axiosWithAuth.delete(`${API_ROUTES.PROJECTS}/${id}`);
      const backendMessage = res.data?.message;

      dispatch(
        showFeedback({
          type: 'success',
          title: 'Project archived',
          message: backendMessage || 'Project has been archived.',
        })
      );

      return id;
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Delete failed';

      dispatch(
        showFeedback({
          type: 'error',
          title: 'Project delete failed',
          message,
        })
      );

      return rejectWithValue(message);
    }
  }
);

// Slice
const projectSlice = createSlice({
  name: 'projects',
  initialState: {
    list: [],
    selectedProject: null,
    status: 'idle',
    error: null,
  },
  reducers: {
    resetProjects: (state) => {
      state.list = [];
      state.selectedProject = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 🔁 All Projects
      .addCase(fetchProjects.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.list = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // ➕ Create Project
      .addCase(createProject.fulfilled, (state, action) => {
        if (action.payload) {
          state.list.unshift(action.payload);
        }
      })

      // 🔍 Fetch Project by ID
      .addCase(fetchProjectById.pending, (state) => {
        state.status = 'loading';
        state.selectedProject = null;
        state.error = null;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.selectedProject = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.selectedProject = null;
      })
      // ✏️ Update
      .addCase(updateProjectDetails.fulfilled, (state, action) => {
        state.selectedProject = action.payload;
        state.status = 'succeeded';

        // also update it in list if present
        if (action.payload?._id) {
          state.list = state.list.map((p) =>
            p._id === action.payload._id ? action.payload : p
          );
        }
      })
      .addCase(updateProjectDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // 🗑️ Delete
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.list = state.list.filter((p) => p._id !== action.payload);
        if (state.selectedProject?._id === action.payload) {
          state.selectedProject = null;
        }
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.error = action.payload;
      });

  },
});

export const { resetProjects } = projectSlice.actions;
export default projectSlice.reducer;